import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../utils/Utils.js';
import ProjectDropdown from '../shared/dropdowns/ProjectDropdown.js';
import FinanceCategoryDropdown from '../shared/dropdowns/FinanceCategoryDropdown.js';
import LotNavRow from '../shared/lots/LotNavRow.js';

// ListProjectLink links a list to a finance project (Trello 8mOHyvSN): items
// then marked purchased with a cost become expenses in that project, filed
// under the chosen finance category ("" = Other). Saved through the API's
// /api/lists/:id/project route, which also files already-purchased items.
// Shown as the list page's Project section: a row that opens the project
// (Trello 70qmkpWY), with an edit button for the link itself.
const ListProjectLink = ({ list, onChange }) => {
  const { t } = useTranslation();
  const [projects,  setProjects]  = useState([]);
  const [editing,   setEditing]   = useState(false);
  const [projectId, setProjectId] = useState(list.project_id || '');
  const [category,  setCategory]  = useState(list.finance_category || '');
  const [error,     setError]     = useState('');

  useEffect(() => {
    Fetch('api/finance/projects', 'GET', null, (data) => setProjects(data || []), () => {});
  }, []);

  const linked = projects.find(p => p._id === list.project_id);
  const categoryLabel = list.finance_category
    ? t(list.finance_category, { defaultValue: list.finance_category })
    : t('other');

  const cancel = () => {
    setEditing(false);
    setError('');
    setProjectId(list.project_id || '');
    setCategory(list.finance_category || '');
  };

  const save = () => {
    Fetch(`api/lists/${list._id}/project`, 'PATCH',
      JSON.stringify({ project_id: projectId, finance_category: projectId ? category : '' }),
      (updated) => {
        setEditing(false);
        setError('');
        onChange?.({ ...list, project_id: updated?.project_id || '', finance_category: updated?.finance_category || '' });
      },
      (message) => setError(message || t('lists_project_link_error'))
    );
  };

  const header = (
    <div className="list-section-header">
      <h3>{t('lists_project')}</h3>
      {!editing && list.status !== 'archived' && (
        <button className="list-items-expiry-edit" onClick={() => setEditing(true)}>
          {list.project_id ? t('edit') : t('lists_link_project')}
        </button>
      )}
    </div>
  );

  if (!editing) {
    return (
      <div className="list-section" data-testid="list-project">
        {header}
        {list.project_id
          ? (
            <div className="wld-nav-list">
              <LotNavRow
                label={linked ? `${linked.code} — ${linked.name}` : '…'}
                summary={categoryLabel}
                to={`/finances/projects/${list.project_id}`} />
            </div>
          )
          : <div className="list-section-empty">{t('lists_no_project')}</div>}
      </div>
    );
  }

  return (
    <div className="list-section">
      {header}
      <div className="list-items-project-edit" data-testid="list-project-edit">
        <ProjectDropdown projects={projects} selected={projectId} onChange={e => setProjectId(e.target.value)} />
        {projectId && (
          <FinanceCategoryDropdown selected={category} onChange={e => setCategory(e.target.value)}
            showAll allLabel={t('lists_finance_category_default')} />
        )}
        <button className="list-items-expiry-save" onClick={save}>{t('save')}</button>
        <button className="list-items-expiry-cancel" onClick={cancel}>{t('cancel')}</button>
        <div className="list-items-project-hint">{t('lists_project_hint')}</div>
        {error && <div className="list-items-project-error">{error}</div>}
      </div>
    </div>
  );
};

export default ListProjectLink;
